import type { Detail, Ik, Logbron, Logregel, Melder, Overzicht, Storing } from '../../gedeeld/types'

/* ===========================================================================
 *  Praten met de eigen server
 *
 *  Alles loopt via de Worker op hetzelfde adres; de browser heeft nergens
 *  anders een sleutel voor. Een fout is een Fout met de tekst van de server,
 *  zoals in Projects: een scherm dat stil "niets" laat zien terwijl de vraag
 *  mislukte, is op een storingspagina het ergste wat er kan.
 * =========================================================================== */

export class Fout extends Error {
  constructor(public status: number, bericht: string) { super(bericht) }
}

let bijUitgelogd: (() => void) | null = null
export function opUitgelogd(f: () => void) { bijUitgelogd = f }

async function lees<T>(a: Response): Promise<T> {
  let lijf: { ok?: boolean; fout?: string } & Record<string, unknown> = {}
  try { lijf = await a.json() } catch { /* geen JSON: dan volgt hieronder de status */ }
  if (a.status === 401) {
    bijUitgelogd?.()
    throw new Fout(401, 'Je bent uitgelogd.')
  }
  if (!a.ok || lijf.ok === false) throw new Fout(a.status, lijf.fout ?? `De server gaf ${a.status}.`)
  return lijf as T
}

async function vraag<T>(pad: string, opties?: { methode?: string; lijf?: unknown }): Promise<T> {
  let a: Response
  try {
    a = await fetch('/api/' + pad, {
      method: opties?.methode ?? 'GET',
      headers: opties?.lijf !== undefined ? { 'Content-Type': 'application/json' } : { Accept: 'application/json' },
      body: opties?.lijf !== undefined ? JSON.stringify(opties.lijf) : undefined,
    })
  } catch {
    throw new Fout(0, 'Geen verbinding met Health. Controleer je internet.')
  }
  return lees<T>(a)
}

const qs = (w: Record<string, string | number | undefined | null>) => {
  const s = new URLSearchParams()
  for (const [k, v] of Object.entries(w)) if (v !== undefined && v !== null && v !== '') s.set(k, String(v))
  const t = s.toString()
  return t ? '?' + t : ''
}

export interface Sessie { ik: Ik; verlooptOp: number; terugval: boolean }

/** Null als er niemand is ingelogd. Gooit niet: dit is de eerste vraag. */
export async function haalIk(): Promise<Sessie | { fout: string } | null> {
  try {
    const a = await fetch('/api/ik', { headers: { Accept: 'application/json' } })
    if (a.status === 401) return null
    const lijf = await a.json().catch(() => null) as ({ ok?: boolean; fout?: string } & Sessie) | null
    if (!a.ok || !lijf?.ok) return { fout: lijf?.fout ?? `De server gaf ${a.status}.` }
    return lijf
  } catch {
    return { fout: 'Geen verbinding met Health.' }
  }
}

export async function uitloggen(): Promise<void> {
  try { await fetch('/api/uitloggen', { method: 'POST' }) } catch { /* vervalt vanzelf */ }
}

// deno-lint-ignore no-explicit-any
type Los = Record<string, any>

export interface Geheim { naam: string; gezet: boolean; nodig: boolean; voor: string }
export interface Persoon { id: string; naam: string; email: string; rollen: string[]; seintjes: boolean; eigenKeuze: boolean }

export const api = {
  overzicht: () => vraag<Overzicht & { beschikbaar: Record<string, number | null> }>('overzicht'),
  controle: (id: string, uren = 24) => vraag<Detail & { info: Los | null }>(`controle/${encodeURIComponent(id)}${qs({ uren })}`),
  storingen: (open = false) => vraag<{ storingen: Storing[] }>(`storingen${qs({ open: open ? 1 : null })}`).then((x) => x.storingen),
  erken: (id: string, notitie: string) => vraag(`storing/${encodeURIComponent(id)}/erken`, { methode: 'POST', lijf: { notitie } }),
  logboek: (f: { bronnen?: string[]; vanaf?: number; tot?: number; niveaus?: string[]; zoek?: string; limiet?: number }) =>
    vraag<{ regels: Logregel[]; fouten: Record<string, string>; bronnen: Logbron[]; vanaf: number; tot: number }>(`logboek${qs({
      bronnen: f.bronnen?.join(','), vanaf: f.vanaf, tot: f.tot, niveaus: f.niveaus?.join(','), zoek: f.zoek, limiet: f.limiet,
    })}`),
  bronnen: () => vraag<{ bronnen: Logbron[] }>('bronnen').then((x) => x.bronnen),
  laatste: (sleutel: 'pg' | 'extra') => vraag<{ at: number | null; inhoud: Los | null }>(`laatste/${sleutel}`),
  database: () => vraag<{ database: Los }>('database').then((x) => x.database),
  configuratie: () => vraag<{ functies: { geheimen: Geheim[]; functie: Los } | null; functiesFout: string | null; worker: Geheim[] }>('configuratie'),
  melders: () => vraag<{ melders: Melder[] }>('melders').then((x) => x.melders),
  mensen: () => vraag<{ mensen: Persoon[] }>('mensen').then((x) => x.mensen),
  voorkeur: () => vraag<{ seintjes: boolean; eigenKeuze: boolean }>('voorkeur'),
  zetVoorkeur: (seintjes: boolean) => vraag<{ seintjes: boolean }>('voorkeur', { methode: 'POST', lijf: { seintjes } }),
  ronde: () => vraag<{ at: number; duurMs: number; telling: Los }>('ronde', { methode: 'POST', lijf: {} }),
}
